import { useCallback, useEffect, useState } from "react";
import {
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Button,
  Chip,
  Box,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Table from "examples/Tables/Table";
import toast from "react-hot-toast";
import apiService from "services/api";
import { getAuthUserId } from "utils/authStorage";

const DAY_OPTIONS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const emptyCategoryForm = { name: "", amount: "", icon: null };
const emptyPlanForm = {
  name: "",
  description: "",
  price: "",
  mrp: "",
  discount: "",
  durationInDays: "",
  totalThali: "",
  breakfast: false,
  lunch: false,
  dinner: false,
  days: [],
  categoryId: "",
  city: [],
  features: [],
  disable: false,
};

function HmtPlans() {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);

  const [categories, setCategories] = useState([]);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState(emptyCategoryForm);

  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [plans, setPlans] = useState([]);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState(emptyPlanForm);
  const [featureInput, setFeatureInput] = useState("");

  const [states, setStates] = useState([]);
  const [selectedStateId, setSelectedStateId] = useState("");
  const [cities, setCities] = useState([]);

  const categoryImageUrl = (icon) => {
    if (!icon) return null;
    if (icon.startsWith("http")) return icon;
    const base = process.env.REACT_APP_IMG || "";
    return `${base.replace(/\/$/, "")}/${String(icon).replace(/^\//, "")}`;
  };

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getHmtCategories();
      const result = response?.data;
      if (!result?.success) {
        throw new Error(result?.message || "Failed to fetch categories");
      }
      const list = Array.isArray(result.data) ? result.data : [];
      setCategories(list);
      if (!selectedCategoryId && list.length > 0) {
        setSelectedCategoryId(list[0]._id);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCategoryId]);

  const fetchPlans = useCallback(async (categoryId) => {
    if (!categoryId) {
      setPlans([]);
      return;
    }
    try {
      setLoading(true);
      const response = await apiService.getHmtPlansByCategory(categoryId);
      const result = response?.data;
      if (!result?.success) {
        throw new Error(result?.message || "Failed to fetch plans");
      }
      setPlans(Array.isArray(result.data) ? result.data : []);
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStates = useCallback(async () => {
    try {
      const response = await apiService.getStates();
      const result = response?.data;
      if (result?.success && Array.isArray(result.data)) {
        setStates(result.data);
        if (result.data.length > 0) {
          setSelectedStateId(result.data[0].id || result.data[0]._id);
        }
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchCities = useCallback(async (stateId) => {
    if (!stateId) {
      setCities([]);
      return;
    }
    try {
      const response = await apiService.getCities(stateId);
      const result = response?.data;
      if (result?.success && Array.isArray(result.data)) {
        setCities(result.data);
      }
    } catch (error) {
      console.error(error);
      setCities([]);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
    fetchStates();
  }, [fetchCategories, fetchStates]);

  useEffect(() => {
    if (selectedCategoryId) {
      fetchPlans(selectedCategoryId);
    }
  }, [selectedCategoryId, fetchPlans]);

  useEffect(() => {
    if (selectedStateId) {
      fetchCities(selectedStateId);
    }
  }, [selectedStateId, fetchCities]);

  const openCreateCategory = () => {
    setEditingCategory(null);
    setCategoryForm(emptyCategoryForm);
    setCategoryDialogOpen(true);
  };

  const openEditCategory = (cat) => {
    setEditingCategory(cat);
    setCategoryForm({ name: cat.name || "", amount: cat.amount ?? "", icon: null });
    setCategoryDialogOpen(true);
  };

  const saveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error("Category name is required");
      return;
    }
    try {
      const formData = new FormData();
      formData.append("name", categoryForm.name.trim());
      if (categoryForm.amount !== "") {
        formData.append("amount", String(categoryForm.amount));
      }
      if (categoryForm.icon) {
        formData.append("icon", categoryForm.icon);
      }
      if (editingCategory) {
        await apiService.updateHmtCategory(editingCategory._id, formData);
        toast.success("Category updated");
      } else {
        await apiService.createHmtCategory(formData);
        toast.success("Category created");
      }
      setCategoryDialogOpen(false);
      fetchCategories();
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message);
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try {
      await apiService.deleteHmtCategory(id);
      toast.success("Category deleted");
      if (selectedCategoryId === id) {
        setSelectedCategoryId("");
      }
      fetchCategories();
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message);
    }
  };

  const openCreatePlan = () => {
    setEditingPlan(null);
    setPlanForm({
      ...emptyPlanForm,
      categoryId: selectedCategoryId,
    });
    setFeatureInput("");
    setPlanDialogOpen(true);
  };

  const openEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      name: plan.name || "",
      description: plan.description || "",
      price: plan.price ?? "",
      mrp: plan.mrp ?? "",
      discount: plan.discount ?? "",
      durationInDays: plan.durationInDays ?? "",
      totalThali: plan.totalThali ?? "",
      breakfast: plan.breakfast ?? false,
      lunch: plan.lunch ?? false,
      dinner: plan.dinner ?? false,
      days: plan.days || [],
      categoryId: plan.categoryId?._id || plan.categoryId || selectedCategoryId,
      city: (plan.city || []).map((c) => (typeof c === "object" ? c._id : c)),
      features: plan.features || [],
      disable: plan.disable ?? false,
    });
    setFeatureInput("");
    setPlanDialogOpen(true);
  };

  const addFeature = () => {
    const value = featureInput.trim();
    if (!value) return;
    if (planForm.features.includes(value)) return;
    setPlanForm((prev) => ({ ...prev, features: [...prev.features, value] }));
    setFeatureInput("");
  };

  const savePlan = async () => {
    const userId = getAuthUserId();
    if (!userId) {
      toast.error("Not signed in");
      return;
    }
    if (!planForm.name.trim() || !planForm.categoryId) {
      toast.error("Name and category are required");
      return;
    }
    const payload = {
      name: planForm.name.trim(),
      description: planForm.description || "",
      price: Number(planForm.price),
      mrp: Number(planForm.mrp),
      discount: Number(planForm.discount),
      durationInDays: Number(planForm.durationInDays),
      totalThali: Number(planForm.totalThali),
      breakfast: planForm.breakfast,
      lunch: planForm.lunch,
      dinner: planForm.dinner,
      days: planForm.days,
      categoryId: planForm.categoryId,
      city: planForm.city,
      features: planForm.features,
      createdBy: userId,
    };
    if (editingPlan) {
      payload.disable = planForm.disable;
      if (planForm.city.length) {
        payload.cities = planForm.city;
      }
    }
    try {
      if (editingPlan) {
        await apiService.updateHmtPlan(editingPlan._id, payload);
        toast.success("Plan updated");
      } else {
        await apiService.createHmtPlan(payload);
        toast.success("Plan created");
      }
      setPlanDialogOpen(false);
      fetchPlans(selectedCategoryId);
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message);
    }
  };

  const categoryColumns = [
    { name: "icon", align: "center" },
    { name: "name", align: "left" },
    { name: "amount", align: "center" },
    { name: "actions", align: "center" },
  ];

  const categoryRows = categories.map((cat) => ({
    icon: cat.icon ? (
      <Box
        component="img"
        src={categoryImageUrl(cat.icon)}
        alt={cat.name}
        sx={{ width: 40, height: 40, objectFit: "cover", borderRadius: 1 }}
      />
    ) : (
      "—"
    ),
    name: (
      <SoftTypography variant="button" fontWeight="medium">
        {cat.name}
      </SoftTypography>
    ),
    amount: (
      <SoftTypography variant="caption">₹{cat.amount ?? 0}</SoftTypography>
    ),
    actions: (
      <SoftBox display="flex" gap={0.5} justifyContent="center">
        <IconButton size="small" onClick={() => openEditCategory(cat)}>
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => deleteCategory(cat._id)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </SoftBox>
    ),
  }));

  const planColumns = [
    { name: "code", align: "left" },
    { name: "name", align: "left" },
    { name: "price", align: "center" },
    { name: "thali", align: "center" },
    { name: "duration", align: "center" },
    { name: "actions", align: "center" },
  ];

  const planRows = plans.map((plan) => ({
    code: (
      <SoftTypography variant="caption" color="secondary">
        {plan.code || "—"}
      </SoftTypography>
    ),
    name: (
      <SoftTypography variant="button" fontWeight="medium">
        {plan.name}
        {plan.disable ? " (disabled)" : ""}
      </SoftTypography>
    ),
    price: <SoftTypography variant="caption">₹{plan.price}</SoftTypography>,
    thali: <SoftTypography variant="caption">{plan.totalThali}</SoftTypography>,
    duration: (
      <SoftTypography variant="caption">{plan.durationInDays} days</SoftTypography>
    ),
    actions: (
      <IconButton size="small" onClick={() => openEditPlan(plan)}>
        <EditIcon fontSize="small" />
      </IconButton>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={2} display="flex" justifyContent="space-between" alignItems="center">
          <SoftBox>
            <SoftTypography variant="h5" fontWeight="bold">
              HMT Plans & Categories
            </SoftTypography>
            <SoftTypography variant="body2" color="text">
              Manage thali categories and subscription plans
            </SoftTypography>
          </SoftBox>
          <Tooltip title="Refresh">
            <span>
              <IconButton
                onClick={() => {
                  fetchCategories();
                  if (selectedCategoryId) fetchPlans(selectedCategoryId);
                }}
                disabled={loading}
                size="small"
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </SoftBox>

        <Card>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab label="Categories" />
            <Tab label="Plans" />
          </Tabs>

          <SoftBox p={2}>
            {loading && (
              <SoftBox display="flex" justifyContent="center" py={4}>
                <CircularProgress size={32} />
              </SoftBox>
            )}

            {!loading && tab === 0 && (
              <SoftBox>
                <SoftBox mb={2} display="flex" justifyContent="flex-end">
                  <SoftButton variant="gradient" color="info" size="small" onClick={openCreateCategory}>
                    <AddIcon sx={{ mr: 0.5 }} fontSize="small" /> Add Category
                  </SoftButton>
                </SoftBox>
                <Table columns={categoryColumns} rows={categoryRows} />
              </SoftBox>
            )}

            {!loading && tab === 1 && (
              <SoftBox>
                <SoftBox mb={2} display="flex" gap={2} flexWrap="wrap" alignItems="center">
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={selectedCategoryId}
                      label="Category"
                      onChange={(e) => setSelectedCategoryId(e.target.value)}
                    >
                      {categories.map((cat) => (
                        <MenuItem key={cat._id} value={cat._id}>
                          {cat.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <SoftButton
                    variant="gradient"
                    color="info"
                    size="small"
                    onClick={openCreatePlan}
                    disabled={!selectedCategoryId}
                  >
                    <AddIcon sx={{ mr: 0.5 }} fontSize="small" /> Add Plan
                  </SoftButton>
                </SoftBox>
                <Table columns={planColumns} rows={planRows} />
              </SoftBox>
            )}
          </SoftBox>
        </Card>
      </SoftBox>

      <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCategory ? "Edit Category" : "New Category"}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label="Name"
            value={categoryForm.name}
            onChange={(e) => setCategoryForm((p) => ({ ...p, name: e.target.value }))}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Amount"
            type="number"
            value={categoryForm.amount}
            onChange={(e) => setCategoryForm((p) => ({ ...p, amount: e.target.value }))}
          />
          <TextField
            fullWidth
            margin="dense"
            type="file"
            label="Icon"
            InputLabelProps={{ shrink: true }}
            onChange={(e) =>
              setCategoryForm((p) => ({ ...p, icon: e.target.files?.[0] || null }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
          <Button onClick={saveCategory} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={planDialogOpen} onClose={() => setPlanDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingPlan ? "Edit Plan" : "New Plan"}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label="Name"
            value={planForm.name}
            onChange={(e) => setPlanForm((p) => ({ ...p, name: e.target.value }))}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Description"
            multiline
            rows={2}
            value={planForm.description}
            onChange={(e) => setPlanForm((p) => ({ ...p, description: e.target.value }))}
          />
          <SoftBox display="flex" gap={1} flexWrap="wrap">
            <TextField
              margin="dense"
              label="Price"
              type="number"
              value={planForm.price}
              onChange={(e) => setPlanForm((p) => ({ ...p, price: e.target.value }))}
            />
            <TextField
              margin="dense"
              label="MRP"
              type="number"
              value={planForm.mrp}
              onChange={(e) => setPlanForm((p) => ({ ...p, mrp: e.target.value }))}
            />
            <TextField
              margin="dense"
              label="Discount"
              type="number"
              value={planForm.discount}
              onChange={(e) => setPlanForm((p) => ({ ...p, discount: e.target.value }))}
            />
            <TextField
              margin="dense"
              label="Duration (days)"
              type="number"
              value={planForm.durationInDays}
              onChange={(e) => setPlanForm((p) => ({ ...p, durationInDays: e.target.value }))}
            />
            <TextField
              margin="dense"
              label="Total Thali"
              type="number"
              value={planForm.totalThali}
              onChange={(e) => setPlanForm((p) => ({ ...p, totalThali: e.target.value }))}
            />
          </SoftBox>

          <FormControl fullWidth margin="dense">
            <InputLabel>Category</InputLabel>
            <Select
              value={planForm.categoryId}
              label="Category"
              onChange={(e) => setPlanForm((p) => ({ ...p, categoryId: e.target.value }))}
            >
              {categories.map((cat) => (
                <MenuItem key={cat._id} value={cat._id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="dense">
            <InputLabel>Days</InputLabel>
            <Select
              multiple
              value={planForm.days}
              label="Days"
              onChange={(e) =>
                setPlanForm((p) => ({ ...p, days: e.target.value }))
              }
            >
              {DAY_OPTIONS.map((day) => (
                <MenuItem key={day} value={day}>
                  {day}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <SoftBox display="flex" gap={2} mt={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={planForm.breakfast}
                  onChange={(e) =>
                    setPlanForm((p) => ({ ...p, breakfast: e.target.checked }))
                  }
                />
              }
              label="Breakfast"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={planForm.lunch}
                  onChange={(e) =>
                    setPlanForm((p) => ({ ...p, lunch: e.target.checked }))
                  }
                />
              }
              label="Lunch"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={planForm.dinner}
                  onChange={(e) =>
                    setPlanForm((p) => ({ ...p, dinner: e.target.checked }))
                  }
                />
              }
              label="Dinner"
            />
            {editingPlan && (
              <FormControlLabel
                control={
                  <Switch
                    checked={planForm.disable}
                    onChange={(e) =>
                      setPlanForm((p) => ({ ...p, disable: e.target.checked }))
                    }
                  />
                }
                label="Disabled"
              />
            )}
          </SoftBox>

          <SoftBox mt={1} display="flex" gap={1} flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>State</InputLabel>
              <Select
                value={selectedStateId}
                label="State"
                onChange={(e) => setSelectedStateId(e.target.value)}
              >
                {states.map((s) => (
                  <MenuItem key={s.id || s._id} value={s.id || s._id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Cities</InputLabel>
              <Select
                multiple
                value={planForm.city}
                label="Cities"
                onChange={(e) => setPlanForm((p) => ({ ...p, city: e.target.value }))}
              >
                {cities.map((c) => (
                  <MenuItem key={c._id} value={c._id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </SoftBox>

          <SoftBox mt={1}>
            <TextField
              size="small"
              label="Add feature"
              value={featureInput}
              onChange={(e) => setFeatureInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addFeature();
                }
              }}
            />
            <Button size="small" onClick={addFeature} sx={{ ml: 1 }}>Add</Button>
            <SoftBox mt={1} display="flex" gap={0.5} flexWrap="wrap">
              {planForm.features.map((f) => (
                <Chip
                  key={f}
                  label={f}
                  size="small"
                  onDelete={() =>
                    setPlanForm((p) => ({
                      ...p,
                      features: p.features.filter((x) => x !== f),
                    }))
                  }
                />
              ))}
            </SoftBox>
          </SoftBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanDialogOpen(false)}>Cancel</Button>
          <Button onClick={savePlan} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default HmtPlans;
